import { ensureError } from '@passionware/platform-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { UploadService } from 'v5/services/io/UploadService/UploadService';
import type { UploadedMedia } from './uploadedMedia';

/** A file on its way to `UploadService`, or one that didn't make it. */
export type PendingUpload =
  | {
      id: string;
      file: File;
      previewUrl: string | null;
      status: 'uploading';
      progress: number;
    }
  | {
      id: string;
      file: File;
      previewUrl: string | null;
      status: 'failed';
      error: Error;
    };

export type UploadOutcome =
  | { type: 'uploaded'; media: UploadedMedia }
  /** `id` names the failed upload, still listed until dismissed. */
  | { type: 'failed'; id: string; error: Error };

let nextUploadId = 0;

/**
 * Uploads files and tracks each while it runs: progress and a local preview
 * of an image. An uploaded file leaves the list and comes back as
 * `UploadedMedia` carrying that preview, which its holder revokes when it
 * lets the file go; a failed one stays until dismissed, so the field can show
 * it and offer a retry.
 */
export function useMediaUploads(
  uploadService: UploadService,
  {
    secure = false
  }: {
    /** Private files (an entity's documents); public ones (callouts, avatars) by default. */
    secure?: boolean;
  } = {}
) {
  const [uploads, setUploads] = useState<PendingUpload[]>([]);
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;

  useEffect(
    () => () =>
      uploadsRef.current.forEach(upload => {
        if (upload.previewUrl) URL.revokeObjectURL(upload.previewUrl);
      }),
    []
  );

  const upload = useCallback(
    async (file: File): Promise<UploadOutcome> => {
      const id = `upload-${++nextUploadId}`;
      const previewUrl = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : null;
      setUploads(prev => [
        ...prev,
        { id, file, previewUrl, status: 'uploading', progress: 0 }
      ]);
      try {
        const result = await uploadService.uploadMedia({
          media: file,
          secure,
          onProgress: progress =>
            setUploads(prev =>
              prev.map(u =>
                u.id === id && u.status === 'uploading' ? { ...u, progress } : u
              )
            )
        });
        setUploads(prev => prev.filter(u => u.id !== id));
        return {
          type: 'uploaded',
          media: {
            fileId: result.fileId,
            accessUrl: result.accessUrl,
            name: file.name,
            mimeType: file.type || undefined,
            previewUrl: previewUrl ?? undefined
          }
        };
      } catch (cause) {
        const error = ensureError(cause);
        setUploads(prev =>
          prev.map(u =>
            u.id === id ? { id, file, previewUrl, status: 'failed', error } : u
          )
        );
        return { type: 'failed', id, error };
      }
    },
    [uploadService, secure]
  );

  const dismiss = useCallback((id: string) => {
    const upload = uploadsRef.current.find(u => u.id === id);
    if (upload?.previewUrl) URL.revokeObjectURL(upload.previewUrl);
    setUploads(prev => prev.filter(u => u.id !== id));
  }, []);

  return {
    uploads,
    upload,
    dismiss,
    isUploading: uploads.some(u => u.status === 'uploading')
  };
}
